import type {
  ApiErrorResponse,
  Appointment,
  AppointmentWithDisplay,
  BulkCancelRequest,
  BulkCancelResponse,
  ClientConfigResponse,
  CreateAppointmentRequest,
  CreateLocationRequest,
  CreatePatientRequest,
  CreatePaymentRequest,
  CreatePushSubscriptionRequest,
  CreateSeriesRequest,
  CreateSeriesResponse,
  DeclareQuarterResponse,
  DeletePatientResponse,
  DeleteSeriesResponse,
  DeletedResponse,
  FeasibilityResponse,
  FeatureRequest,
  GeocodeMissingResponse,
  GeocodeResponse,
  LocalDate,
  LocalDateTime,
  LocalMonth,
  LocalQuarter,
  Location,
  LocationChoice,
  MeteoDayResponse,
  MeteoWeekResponse,
  IssueNoteRequest,
  LoginResponse,
  LogoutResponse,
  NavCounts,
  Note,
  Patient,
  PatientAccountResponse,
  Payment,
  PersoEventsResponse,
  PushPublicKeyResponse,
  PushSendResponse,
  PushSubscriptionRecord,
  ReplaceSeriesRequest,
  ReplaceSeriesResponse,
  ReportQuarterResponse,
  ReportResponse,
  RoutePreviewResponse,
  SchoolHolidaysResponse,
  SendNoteResponse,
  SeriesWithDisplay,
  SessionResponse,
  Settings,
  SmtpTestResponse,
  TravelTimesResponse,
  UpdateAppointmentRequest,
  UpdateLocationRequest,
  UpdatePatientRequest,
  UpdateSettingsRequest,
} from '@ergo/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code = 'error',
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The auth provider plugs in here so an expired session drops back to the login screen. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

type QueryValue = string | number | boolean | undefined;

function withQuery(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `${path}?${serialized}` : path;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    // Fastify refuses a declared JSON body that is not there, so bodyless calls send no header.
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorResponse | null;
    if (response.status === 401 && path !== '/login') onUnauthorized?.();
    throw new ApiError(response.status, body?.message ?? 'Erreur inattendue', body?.error);
  }

  return (await response.json()) as T;
}

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export const api = {
  session: () => request<SessionResponse>('/session'),
  /** Les trois pastilles de la barre de navigation, en un appel. */
  navCounts: () => request<NavCounts>('/nav-counts'),
  login: (password: string) => send<LoginResponse>('POST', '/login', { password }),
  logout: () => send<LogoutResponse>('POST', '/logout'),

  patients: {
    list: (query?: { active?: boolean }) =>
      request<Patient[]>(withQuery('/patients', { active: query?.active })),
    get: (id: number) => request<Patient>(`/patients/${id}`),
    create: (body: CreatePatientRequest) => send<Patient>('POST', '/patients', body),
    update: (id: number, body: UpdatePatientRequest) =>
      send<Patient>('PATCH', `/patients/${id}`, body),
    remove: (id: number) => send<DeletePatientResponse>('DELETE', `/patients/${id}`),

    addLocation: (patientId: number, body: CreateLocationRequest) =>
      send<Location>('POST', `/patients/${patientId}/locations`, body),
    updateLocation: (patientId: number, locationId: number, body: UpdateLocationRequest) =>
      send<Location>('PATCH', `/patients/${patientId}/locations/${locationId}`, body),
    removeLocation: (patientId: number, locationId: number) =>
      send<DeletedResponse>('DELETE', `/patients/${patientId}/locations/${locationId}`),

    /** Le panneau « Compte » : le solde, et le relevé derrière lui. */
    account: (id: number) => request<PatientAccountResponse>(`/patients/${id}/account`),
  },

  /**
   * Le grand livre : un montant et une date, jamais affectés à une note. Supprimable —
   * c'est son livre de comptes, pas un document légal.
   */
  payments: {
    /** Les plus récents d'abord. */
    list: (patientId: number) => request<Payment[]>(`/patients/${patientId}/payments`),
    create: (patientId: number, body: CreatePaymentRequest) =>
      send<Payment>('POST', `/patients/${patientId}/payments`, body),
    remove: (id: number) => send<DeletedResponse>('DELETE', `/payments/${id}`),
  },

  appointments: {
    /** `from` and `to` are inclusive day bounds. */
    list: (query: { from: LocalDate; to: LocalDate; patientId?: number }) =>
      request<AppointmentWithDisplay[]>(withQuery('/appointments', { ...query })),
    /** Les séances passées jamais pointées : la liste de Pointage, et sa pastille. */
    unlogged: () => request<AppointmentWithDisplay[]>('/appointments/unlogged'),
    get: (id: number) => request<AppointmentWithDisplay>(`/appointments/${id}`),
    create: (body: CreateAppointmentRequest) => send<Appointment>('POST', '/appointments', body),
    update: (id: number, body: UpdateAppointmentRequest) =>
      send<Appointment>('PATCH', `/appointments/${id}`, body),
    remove: (id: number) => send<DeletedResponse>('DELETE', `/appointments/${id}`),
    bulkCancel: (body: BulkCancelRequest) =>
      send<BulkCancelResponse>('POST', '/appointments/bulk-cancel', body),
  },

  series: {
    list: (query?: { patientId?: number }) =>
      request<SeriesWithDisplay[]>(withQuery('/series', { patientId: query?.patientId })),
    get: (id: number) => request<SeriesWithDisplay>(`/series/${id}`),
    create: (body: CreateSeriesRequest) => send<CreateSeriesResponse>('POST', '/series', body),
    /** The "toutes les séances à venir" edit. */
    replace: (id: number, body: ReplaceSeriesRequest) =>
      send<ReplaceSeriesResponse>('POST', `/series/${id}/replace`, body),
    remove: (id: number, query?: { from?: LocalDate }) =>
      send<DeleteSeriesResponse>('DELETE', withQuery(`/series/${id}`, { from: query?.from })),
  },

  report: {
    get: (month: LocalMonth) => request<ReportResponse>(withQuery('/report', { month })),
    /** Un trimestre URSSAF : le total encaissé, l'échéance, et les notes encaissées. */
    quarter: (q: LocalQuarter) => request<ReportQuarterResponse>(withQuery('/report/quarter', { q })),
    /** « C'est déclaré » — idempotent : redéclarer renvoie la date du premier passage. */
    declareQuarter: (q: LocalQuarter) =>
      send<DeclareQuarterResponse>('PATCH', `/report/quarter/${q}/declare`),
  },

  /**
   * Notes d'honoraires. No update and no remove on purpose: a delivered note is
   * immutable, and it carries no paid state — money lives on the patient account.
   */
  notes: {
    list: (query?: { patientId?: number; month?: LocalMonth }) =>
      request<Note[]>(withQuery('/notes', { ...query })),
    /** The server picks the séances: the month's effectuées not yet on a note. */
    issue: (body: IssueNoteRequest) => send<Note>('POST', '/notes', body),
    send: (id: number) => send<SendNoteResponse>('POST', `/notes/${id}/send`),
    /** A plain link the browser downloads; the API client never reads the bytes. */
    pdfUrl: (id: number) => `/api/notes/${id}/pdf`,
  },

  settings: {
    get: () => request<Settings>('/settings'),
    update: (body: UpdateSettingsRequest) => send<Settings>('PATCH', '/settings', body),
    /** Une nouvelle adresse de flux : l'ancienne cesse de répondre aussitôt. */
    regenerateFeed: () => send<Settings>('POST', '/settings/seances-feed/regenerate'),
  },

  /**
   * Son agenda Google personnel, en lecture seule : de quoi ne pas poser une séance
   * sur un rendez-vous perso. Bornes de jours inclusives, comme les séances.
   */
  perso: {
    list: (query: { from: LocalDate; to: LocalDate }) =>
      request<PersoEventsResponse>(withQuery('/perso', { ...query })),
  },

  /**
   * Les vacances scolaires de sa zone : un décor de plus, jamais une contrainte.
   * Bornes de jours inclusives, comme les séances ; la fin d'une période, elle,
   * reste exclue — c'est le jour de la rentrée.
   */
  holidays: {
    list: (query: { from: LocalDate; to: LocalDate }) =>
      request<SchoolHolidaysResponse>(withQuery('/holidays', { ...query })),
  },

  meteo: {
    day: (date: LocalDate) => request<MeteoDayResponse>(withQuery('/meteo', { date })),
    week: (from: LocalDate) => request<MeteoWeekResponse>(withQuery('/meteo/week', { from })),
  },

  smtp: {
    /** Emails the configured address itself: the only way to know Gmail accepts it. */
    test: () => send<SmtpTestResponse>('POST', '/smtp/test'),
  },

  maps: {
    config: () => request<ClientConfigResponse>('/config'),
    /** Preview for the location form; the stored coordinates come from the save. */
    geocode: (address: string) => request<GeocodeResponse>(withQuery('/geocode', { address })),
    geocodeMissing: () => send<GeocodeMissingResponse>('POST', '/locations/geocode-missing'),
    travelTimes: (date: LocalDate) =>
      request<TravelTimesResponse>(withQuery('/travel-times', { date })),
    feasibility: (query: {
      locationId: LocationChoice;
      startsAt: LocalDateTime;
      durationMin: number;
      excludeId?: number;
    }) => request<FeasibilityResponse>(withQuery('/appointments/feasibility', { ...query })),
    /** The day the RDV editor previews: the day as it stands, and with this séance in it. */
    routePreview: (query: {
      date: LocalDate;
      locationId: LocationChoice;
      /** Needed with a visio: the patient may not have its row yet. */
      patientId?: number;
      startsAt: LocalDateTime;
      durationMin: number;
      excludeId?: number;
    }) => request<RoutePreviewResponse>(withQuery('/route-preview', { ...query })),
  },

  featureRequests: {
    list: () => request<FeatureRequest[]>('/feature-requests'),
    create: (text: string) => send<FeatureRequest>('POST', '/feature-requests', { text }),
    remove: (id: number) => send<DeletedResponse>('DELETE', `/feature-requests/${id}`),
  },

  push: {
    publicKey: () => request<PushPublicKeyResponse>('/push/public-key'),
    devices: () => request<PushSubscriptionRecord[]>('/push/subscriptions'),
    subscribe: (body: CreatePushSubscriptionRequest) =>
      send<PushSubscriptionRecord>('POST', '/push/subscriptions', body),
    removeDevice: (id: number) => send<DeletedResponse>('DELETE', `/push/subscriptions/${id}`),
    test: () => send<PushSendResponse>('POST', '/push/test'),
  },
};

export function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : 'Erreur inattendue';
}
